import React, { ReactElement, ReactNode, useCallback, useContext, useMemo } from "react";
import {
  CMEProvider as CMEProviderOG,
  useCmeContext as useCmeContextOG,
} from "@rotorstudios/fe-cme";

import {
  CME_APPLICABILITY_DATA,
  CME_ASSETS_DATA,
  CME_OPTIONSET_ACCESSORIES,
  CME_OPTIONSET_COLOUR,
  CME_OPTIONSET_ENGINE,
  CME_OPTIONSET_MVOB,
  CME_OPTIONSET_TRIM,
  CME_ROOT_PATH,
} from "../Data/static";
import type { iInitConfig, iMaterialCode } from "../Data/types";
import { removeDuplicatesInArray } from "../../helpers/utils";
import { getSiloProductAndVersion } from "../../helpers/cmeHelper";
import { useDataContext } from "../Data/DataProvider";

type iCMEContext = {
  setInitialUserConfig: () => void;
  getCurrentUserConfig: () => iInitConfig;
  getMaterialCode: () => iMaterialCode;
  getEngine: () => void;
};

type iCMEProvider = {
  children: ReactNode | ReactNode[];
};

type iProps = {
  children: ReactNode;
  siloManifest?: any;
};

export const CMEContext = React.createContext<iCMEContext | null | any>(null);
export const useCmeContext = () => {
  const context = useContext(CMEContext);

  if (!context) {
    throw new Error("useCmeContext must be used within a CMEProvider");
  }

  return context;
};

// *************
// Provider Wrapper
// *************
export const CMEProvider = (props: iCMEProvider): ReactElement => {
  const { siloManifest } = useDataContext();
  const { children } = props;

  return siloManifest ? (
    <CMEProviderOG
      cmeDataPath={`${CME_ROOT_PATH}${siloManifest.silo_path}${CME_APPLICABILITY_DATA}`}
      cmeAssetsDataPath={`${CME_ROOT_PATH}${siloManifest.silo_path}${CME_ASSETS_DATA}`}
    >
      <EBROCMEProvider siloManifest={siloManifest}>{children}</EBROCMEProvider>
    </CMEProviderOG>
  ) : (
    <EBROCMEProvider siloManifest={siloManifest}>{children}</EBROCMEProvider>
  );
};

// *************
// Provider Extension specific for EBRO
// *************
export const EBROCMEProvider = ({ children, siloManifest }: iProps): ReactElement => {
  const CMEContextOG = useCmeContextOG();

  const splitDependencyList = (dependencies: string[]) => {
    if (dependencies?.length > 0) {
      const list = [] as string[];

      for (const dep of dependencies) {
        list.push(...dep.split(","));
      }

      return removeDuplicatesInArray(list);
    }
    return [];
  };

  const addToArray = (value: string) => {
    if (value === undefined || value === null) {
      return [];
    }

    if (Array.isArray(value)) {
      return value;
    }

    return Array.of(value);
  };

  const getItemsFromConfig = useCallback(
    (config: Record<string, any>) =>
      Object.entries(config).flatMap(([key, value]) =>
        addToArray(value)
          .filter(Boolean)
          .map((item) => ({ key, item })),
      ),
    [],
  );

  const setInitialUserConfig = useCallback(
    async (config: Record<string, any>) => {
      if (config) {
        const initConfig: string[] = [];
        const accessoriesConfig: string[] = [];
        const accessoryDependencies: string[] = [];
        const mvobDependencies = CMEContextOG.getDependentOptions(
          CME_OPTIONSET_MVOB,
          config.mvob,
        );

        const items = getItemsFromConfig(config);

        for (const { key, item } of items) {
          if (key === CME_OPTIONSET_ACCESSORIES) {
            accessoriesConfig.push(item);

            const dependencies = splitDependencyList(
              CMEContextOG.getDependentOptions(key, item),
            );

            accessoryDependencies.push(...dependencies);
          } else {
            initConfig.push(item);
          }
        }

        // Only the dependencies that are present in original configuration
        const filteredAccessoryDeps = accessoryDependencies.filter((opt: string) =>
          accessoriesConfig.includes(opt),
        );

        // Order is important to ensure proper CME selections:
        // mvob dependencies, configuration (minus accessories), accessory dependencies, accessories
        const initialOptions = removeDuplicatesInArray([
          ...mvobDependencies,
          ...initConfig,
          ...filteredAccessoryDeps,
          ...accessoriesConfig,
        ]);

        CMEContextOG.selectOptions(initialOptions);
      }
    },
    [CMEContextOG, getItemsFromConfig],
  );

  const getCurrentUserConfig = useCallback(
    (): iInitConfig => ({
      mvob: CMEContextOG?.cmeOptions?.selectedOptions?.get(CME_OPTIONSET_MVOB) ?? "",
      colour: CMEContextOG?.cmeOptions?.selectedOptions?.get(CME_OPTIONSET_COLOUR) ?? "",
      interiorTrim:
        CMEContextOG?.cmeOptions?.selectedOptions?.get(CME_OPTIONSET_TRIM) ?? "",
      accessories:
        CMEContextOG?.cmeOptions?.selectedOptions?.get(CME_OPTIONSET_ACCESSORIES) ?? [],
    }),
    [CMEContextOG?.cmeOptions?.selectedOptions],
  );

  const getMaterialCode = useCallback((): iMaterialCode => {
    const { mvob, colour, interiorTrim } = getCurrentUserConfig();

    // GR86 GTS specific: Transform AM20L to AM20 for pricing compatibility, see EBRO-5357
    const { productId } = getSiloProductAndVersion(siloManifest) || {};

    const isGR86 = productId?.toUpperCase() === "GR86";
    const pricingTrim = isGR86 && interiorTrim === "AM20L" ? "AM20" : interiorTrim;

    const pricingCode =
      CMEContextOG?.cmeOptions?.optionsToDisplay
        ?.get(CME_OPTIONSET_COLOUR)
        ?.options?.find((option: any) => option.name === colour)["pricing-code"] ??
      undefined;
    return {
      config: `${mvob}${interiorTrim}${colour}`,
      pricing: `${mvob}${pricingTrim}${pricingCode ?? colour}`,
    };
  }, [getCurrentUserConfig, CMEContextOG?.cmeOptions?.optionsToDisplay, siloManifest]);

  const getEngine = useCallback(
    (): string =>
      CMEContextOG?.cmeOptions?.selectedOptions.get(CME_OPTIONSET_ENGINE) ?? "",
    [CMEContextOG?.cmeOptions?.selectedOptions],
  );

  const providerValue = useMemo(
    () => ({
      ...CMEContextOG,
      setInitialUserConfig,
      getCurrentUserConfig,
      getMaterialCode,
      getEngine,
    }),
    [
      CMEContextOG,
      getCurrentUserConfig,
      getEngine,
      getMaterialCode,
      setInitialUserConfig,
    ],
  );

  return <CMEContext.Provider value={providerValue}>{children}</CMEContext.Provider>;
};
